import { Flex, Typography } from "antd";
import { Card } from "@libertify/ui";
import { EXPERIENCE_OBJECTIVES } from "data/tones/";
import { useTranslate } from "#src/hooks";
import { useAntTheme } from "hooks/useAntTheme";

type ObjectivesSelectProps = {
  // `value` and `onChange` are injected by Ant Design's <Form.Item>, which lets
  // this card grid behave like any other controlled form field.
  value?: string[];
  onChange?: (value: string[]) => void;
};

const OBJECTIVE_COLORS: Record<keyof typeof EXPERIENCE_OBJECTIVES, string> = {
  Support: "#14b8a5",
  Persuade: "#6365f1",
  Onboard: "#10b981",
  Inform: "#f59f0b",
  Learn: "#3b83f6",
  Engage: "#ec489a",
  Retain: "#f97516",
};

export const ObjectivesSelect = ({
  value,
  onChange,
}: ObjectivesSelectProps) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const objectives = Object.keys(EXPERIENCE_OBJECTIVES);
  const selected = value ?? [];

  const toggleObjective = (key: string) => {
    if (!onChange) return;
    onChange(
      selected.includes(key)
        ? selected.filter((item) => item !== key)
        : [...selected, key]
    );
  };

  return (
    <Flex gap={12} wrap>
      {objectives.map((key) => {
        const Icon = EXPERIENCE_OBJECTIVES[key]?.icon;
        const isSelected = selected.includes(key);
        return (
          <Card
            key={key}
            padding="small"
            role="button"
            aria-pressed={isSelected}
            onClick={() => toggleObjective(key)}
            style={{
              flex: "1 1 96px",
              minWidth: 96,
              borderColor: isSelected ? theme.colorPrimary : undefined,
              background: isSelected ? theme.colorPrimaryBg : undefined,
            }}
          >
            <Flex vertical align="center" gap={10}>
              <Flex
                align="center"
                justify="center"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  color: theme.colorPrimary,
                  backgroundColor: `${OBJECTIVE_COLORS[key]}1a`,
                }}
              >
                {Icon && (
                  <Icon
                    size={22}
                    weight="duotone"
                    style={{ color: OBJECTIVE_COLORS[key] }}
                  />
                )}
              </Flex>
              <Typography.Text strong>
                {t(`src.data.experienceObjectives.${key}.name`)}
              </Typography.Text>
            </Flex>
          </Card>
        );
      })}
    </Flex>
  );
};
